import React, { useEffect, useRef } from 'react';
import { BrandedAlertModal } from 'components/Modal';
import {
  PaymentPromptCheckpoint,
  PaymentPromptRemediation,
  PaymentPromptSourcePage,
  trackPaymentPromptDismissed,
  trackPaymentPromptShown,
} from 'utils/paymentPromptAnalytics';

interface PaymentRequiredModalProps {
  isOpen: boolean;
  locationId: number | null;
  jobId?: number | string | null;
  offerId?: number | string | null;
  requiresCardSetup: boolean;
  isCardInvalid: boolean;
  paymentPolicy?: 'standard_revenue' | '123dentist_corporate' | null;
  corporateManaged?: boolean;
  paymentUnavailable?: boolean;
  checkpoint: PaymentPromptCheckpoint;
  sourcePage: PaymentPromptSourcePage;
  onGoPaymentInfo: (
    locationId: number | null,
    checkpointOrigin: PaymentPromptCheckpoint,
  ) => void;
  onClose: () => void;
  onRetry?: () => void;
}

const CardHeaderIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <rect x="3" y="6" width="18" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3 10h18" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const CardIllustration: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <rect x="2.5" y="6" width="19" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M2.5 10h19" stroke="currentColor" strokeWidth="1.6" />
    <path d="M9 18.5l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AlertIllustration: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M12 3l10 18H2L12 3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M12 10v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="17.5" r="1" fill="currentColor" />
  </svg>
);

const PAYMENT_MODAL_HISTORY_KEY = '__tempstarsPaymentRequiredModal';

const PaymentRequiredModal: React.FC<PaymentRequiredModalProps> = ({
  isOpen,
  locationId,
  jobId,
  offerId,
  requiresCardSetup,
  isCardInvalid,
  paymentPolicy = null,
  corporateManaged = false,
  paymentUnavailable = false,
  checkpoint,
  sourcePage,
  onGoPaymentInfo,
  onClose,
  onRetry,
}) => {
  const showSetup = !!requiresCardSetup;
  const showInvalid = !!isCardInvalid && !requiresCardSetup;
  const isVisible = isOpen && (showSetup || showInvalid || paymentUnavailable);
  const isCorporatePolicy = paymentPolicy === '123dentist_corporate';
  let paymentRemediation: PaymentPromptRemediation = 'add';
  if (paymentUnavailable) paymentRemediation = 'retry';
  else if (corporateManaged) paymentRemediation = 'corporate_managed';
  else if (showInvalid) paymentRemediation = 'revalidate';
  const impressionTrackedRef = useRef(false);
  const dismissalTrackedRef = useRef(false);
  const interactionRef = useRef<{
    checkpoint: PaymentPromptCheckpoint;
    sourcePage: PaymentPromptSourcePage;
    locationId: number | null;
    jobId?: number | string | null;
    offerId?: number | string | null;
    paymentRemediation: PaymentPromptRemediation;
  } | null>(null);
  const historyEntryRef = useRef<{
    marker: string;
    previousState: unknown;
    url: string;
  } | null>(null);
  const pendingHistoryActionRef = useRef<(() => void) | null>(null);
  const historyActionRequestedRef = useRef(false);
  const browserBackActionRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (!isVisible) {
      impressionTrackedRef.current = false;
      dismissalTrackedRef.current = false;
      interactionRef.current = null;
      historyActionRequestedRef.current = false;
      return;
    }
    if (!impressionTrackedRef.current) {
      interactionRef.current = {
        checkpoint,
        sourcePage,
        locationId,
        jobId,
        offerId,
        paymentRemediation,
      };
      trackPaymentPromptShown(interactionRef.current);
      impressionTrackedRef.current = true;
      dismissalTrackedRef.current = false;
      historyActionRequestedRef.current = false;
    }
  }, [checkpoint, isVisible, jobId, locationId, offerId, paymentRemediation, sourcePage]);

  const trackDismissalOnce = (action: 'cancel' | 'proceed') => {
    if (dismissalTrackedRef.current) return;
    dismissalTrackedRef.current = true;
    trackPaymentPromptDismissed({
      ...(interactionRef.current || {
        checkpoint,
        sourcePage,
        locationId,
        jobId,
        offerId,
        paymentRemediation,
      }),
      action,
    });
  };

  browserBackActionRef.current = () => {
    trackDismissalOnce('cancel');
    onClose();
  };

  useEffect(() => {
    if (!isVisible || typeof window === 'undefined') return undefined;

    const marker = `${Date.now()}-${Math.random()}`;
    const previousState = window.history.state;
    const url = window.location.href;
    try {
      window.history.pushState({
        ...(previousState && typeof previousState === 'object' ? previousState : {}),
        [PAYMENT_MODAL_HISTORY_KEY]: marker,
      }, document.title, url);
      historyEntryRef.current = { marker, previousState, url };
    } catch {
      historyEntryRef.current = null;
    }

    const handlePopState = () => {
      if (!historyEntryRef.current) return;
      historyEntryRef.current = null;
      historyActionRequestedRef.current = true;
      const pendingAction = pendingHistoryActionRef.current;
      pendingHistoryActionRef.current = null;
      (pendingAction || browserBackActionRef.current)();
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      const activeEntry = historyEntryRef.current;
      if (activeEntry && window.history.state?.[PAYMENT_MODAL_HISTORY_KEY] === activeEntry.marker) {
        window.history.replaceState(activeEntry.previousState, document.title, activeEntry.url);
      }
      historyEntryRef.current = null;
      pendingHistoryActionRef.current = null;
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const runAfterClosingHistoryEntry = (action: () => void) => {
    if (historyActionRequestedRef.current) return;
    historyActionRequestedRef.current = true;
    const activeEntry = historyEntryRef.current;
    if (activeEntry && window.history.state?.[PAYMENT_MODAL_HISTORY_KEY] === activeEntry.marker) {
      pendingHistoryActionRef.current = action;
      try {
        window.history.back();
        return;
      } catch {
        pendingHistoryActionRef.current = null;
        historyEntryRef.current = null;
      }
    }
    action();
  };

  const handlePrimary = () => {
    const interaction = interactionRef.current || {
      checkpoint,
      sourcePage,
      locationId,
      jobId,
      offerId,
      paymentRemediation,
    };
    runAfterClosingHistoryEntry(() => {
      trackDismissalOnce('proceed');
      onGoPaymentInfo(interaction.locationId, interaction.checkpoint);
    });
  };
  const handleClose = () => {
    runAfterClosingHistoryEntry(() => {
      trackDismissalOnce('cancel');
      onClose();
    });
  };

  const handleRetry = () => {
    runAfterClosingHistoryEntry(() => {
      trackDismissalOnce('proceed');
      onRetry?.();
    });
  };

  if (paymentUnavailable) {
    return (
      <BrandedAlertModal
        isOpen={isOpen}
        onClose={handleClose}
        headerLabel="Payment Information"
        headerIcon={<CardHeaderIcon />}
        illustration={<AlertIllustration />}
        illustrationVariant="danger"
        title="We Couldn't Verify Payment Information"
        ariaLabel="Unable to verify payment information"
        actions={[
          { label: 'Not now', onClick: handleClose, variant: 'secondary' },
          { label: 'Try again', onClick: handleRetry, variant: 'primary' },
        ]}
      >
        <p>Offer details are still protected. Check your connection and try again.</p>
      </BrandedAlertModal>
    );
  }

  if (corporateManaged) {
    return (
      <BrandedAlertModal
        isOpen={isOpen}
        onClose={handleClose}
        headerLabel="Corporate Payment Information"
        headerIcon={<CardHeaderIcon />}
        illustration={<AlertIllustration />}
        illustrationVariant="danger"
        title="Corporate Payment Information Required"
        ariaLabel="Corporate payment information required"
        actions={[{ label: 'Okay', onClick: handleClose, variant: 'primary' }]}
      >
        <p>The shared 123Dentist corporate card is missing or needs attention.</p>
        <p>Please contact the 123 Corporate Office to update it. This office cannot add a separate card.</p>
      </BrandedAlertModal>
    );
  }

  if (showSetup) {
    return (
      <BrandedAlertModal
        isOpen={isOpen}
        onClose={handleClose}
        headerLabel="Payment Information"
        headerIcon={<CardHeaderIcon />}
        illustration={<CardIllustration />}
        illustrationVariant="success"
        title={isCorporatePolicy ? 'Add Corporate Payment Info to Continue' : 'Add Payment Info to Continue'}
        ariaLabel="Payment information required"
        actions={[
          { label: 'Not now', onClick: handleClose, variant: 'secondary' },
          { label: 'Add payment info', onClick: handlePrimary, variant: 'primary' },
        ]}
      >
        {checkpoint === 'offer_details_view' && !isCorporatePolicy ? (
          <p>Your candidates are ready — add payment info to view offer details. You still pay $0 until a shift is completed.</p>
        ) : isCorporatePolicy ? (
          <>
            <p>Add the shared 123Dentist corporate card to view offer details.</p>
            <p>The card supports Pay Now after a shift; placement fees continue to be invoiced monthly.</p>
          </>
        ) : (
          <>
            <p>You pay $0 today.</p>
            <p>If a placement fee applies, our flat fee is only charged after a shift is completed.</p>
            <p>Any free period or promotion on your office still applies.</p>
          </>
        )}
      </BrandedAlertModal>
    );
  }

  return (
    <BrandedAlertModal
      isOpen={isOpen}
      onClose={handleClose}
      headerLabel="Payment Information"
      headerIcon={<CardHeaderIcon />}
      illustration={<AlertIllustration />}
      illustrationVariant="danger"
      title={isCorporatePolicy ? 'Update Corporate Payment Info to Continue' : 'Update Payment Info to Continue'}
      ariaLabel="Update payment information"
      actions={[
        { label: 'Not now', onClick: handleClose, variant: 'secondary' },
        { label: 'Add payment info', onClick: handlePrimary, variant: 'primary' },
      ]}
    >
      <p>Your current payment method was previously declined.</p>
      {isCorporatePolicy ? (
        <p>Revalidate the shared 123Dentist corporate card used for Pay Now. Placement fees continue to be invoiced monthly.</p>
      ) : (
        <>
          <p>If a placement fee applies, our flat fee is only charged after a shift is completed.</p>
          <p>Any free period or promotion still applies. You pay $0 today.</p>
        </>
      )}
    </BrandedAlertModal>
  );
};

export default PaymentRequiredModal;
